import React, { useEffect, useMemo, useState } from "react";
import { appInsights } from "../../services/Logging/AppInsights";
import { CountryProfileService } from "../../services/CountryProfiles/CountryProfileService";
import {
  ActionButton,
  IIconProps,
  SelectionMode,
  Stack,
  Selection,
  Spinner,
  SpinnerSize,
  SearchBox,
} from "@fluentui/react";
import { Card, CoherenceDataGrid } from "@coherence-design-system/controls";
import { useNavigate } from "react-router-dom";
import {
  CountryProfileInfo,
  ICountriesProfileProps,
} from "./CountriesProfile.type";
import { CustomSpanForListing } from "../Quotation/QuotationStyle";
import { useLocalStorageContext } from "../../Provider/LocalStorageProvider";
import { UserRoles } from "../../common/enum/UserRoles";

const CountryProfileList: React.FunctionComponent<ICountriesProfileProps> = (
  props
) => {
  const AddIcon: IIconProps = { iconName: "Add" };
  const [isLoading, setIsLoading] = useState(true);
  const [countryProfileChange, setCountryProfileChange] = useState(false);
  const [countryProfiles, setCountryProfiles] = useState([]);
  const [filteredCountryProfiles, setFilteredCountryProfiles] = useState([]);
  const navigate = useNavigate();
  const [selectedGridData, setSelectedGridData] = useState<{
    items: any[];
  }>({ items: [] });
  const { userRole } = useLocalStorageContext();
  const CardStyles = {
    layout: {
      margin: 0,
      padding: 0,
      paddingLeft: "30px",
    },
  };

  useEffect(() => {
    setIsLoading(true);
    appInsights.trackEvent({
      name: "In CountryProfileList.tsx, triggering get All Country Profile API",
    });
    CountryProfileService.getAllCountryProfiles()
      .then((res) => {
        if (res !== undefined) {
          appInsights.trackEvent({
            name: "In CountryProfileList.tsx, Countries list feteched successfully.",
          });
          setCountryProfiles(res.data.data);
          setFilteredCountryProfiles(res.data.data);
          setIsLoading(false);
        }
      })
      .catch((error) => {
        appInsights.trackException({
          error,
          properties: {
            name: "In CountryProfileList.tsx, error while getting all country Profiles",
          },
        });
        console.log("Error in CountryProfileList.tsx " + error);
      });
  }, [countryProfileChange]);

  const onClickNewCountryProfile = () => {
    appInsights.trackEvent({
      name: "In CountryProfileList.tsx, Clicked New Country button to create new Country Profile.",
    });
    navigate("/CountryProfile/CountryDetails", {
      state: {
        isCountryProfileFlow: true,
      },
    });
  };
  const searchCountryByName = (searchText: string | undefined) => {
    if (searchText !== undefined) {
      const filteredResults = countryProfiles.filter(
        (countryProfile: CountryProfileInfo) =>
          countryProfile.countryName
            .toLowerCase()
            .includes(searchText.toLowerCase())
      );
      setFilteredCountryProfiles(filteredResults);
    }
  };

  const countryProfileSelection = useMemo(
    () =>
      new Selection({
        onSelectionChanged: () => {
          appInsights.trackEvent({
            name: "In CountryProfileList.tsx, calling countryProfileSelection change on Click checkbox",
            properties: {
              countryProfileSelection: countryProfileSelection.getSelection(),
            },
          });
          const selectedCountry = countryProfileSelection.getSelection();
          if (selectedCountry?.length) {
            setSelectedGridData({ items: selectedCountry });
          }
        },
        selectionMode: SelectionMode.single,
      }),
    []
  );
  if (isLoading) {
    return <Spinner size={SpinnerSize.large} />;
  }

  const onClickAccountId = () => {
    if (selectedGridData?.items?.length) {
      navigate("/CountryProfile/EditDetails", {
        state: selectedGridData?.items[0],
      });
    }
  };
  return (
    <Stack data-testid="countryprofile-page">
      <Stack horizontal>
        <Card styles={CardStyles}>
          <h2>Country Profile</h2>
        </Card>
      </Stack>
      <Stack
        horizontal
        horizontalAlign={userRole === UserRoles.Admin ? "space-between" : "end"}
        style={{
          marginTop: "10px",
          paddingLeft: "70px",
          paddingRight: "70px",
          alignItems: "flex-end",
        }}
      >
        {userRole === UserRoles.Admin && (
          <ActionButton iconProps={AddIcon} onClick={onClickNewCountryProfile}>
            Create New
          </ActionButton>
        )}

        <SearchBox
          placeholder="Search Country"
          onChange={(e, searchText) => {
            searchCountryByName(searchText);
          }}
          style={{ width: '500px', background: "white" }}
        />
      </Stack>
      <Stack style={{ paddingLeft: "70px", paddingRight: "70px" }}>
        <CoherenceDataGrid
          ariaLabelForSelectAllCheckbox="select all checkbox"
          checkButtonAriaLabel="checkbox"
          checkboxVisibility={2}
          compactMode="compact"
          data={
            countryProfiles.length > filteredCountryProfiles.length
              ? filteredCountryProfiles
              : countryProfiles
          }
          isSortable
          isLoading={isLoading}
          listConfig={[
            {
              fieldName: "countryName",
              key: "CountryName",
              name: "Country Name",
              type: "link",
              isResizable: true,
              url: "",
              linkProps: {
                onClick: onClickAccountId,
                style: { textDecoration: "underline" },
              },
            },
            {
              fieldName: "region",
              key: "Region",
              name: "Region",
              type: "string",
              isResizable: true,
            },
            {
              fieldName: "countryCode",
              key: "CountryCode",
              name: "Country Code",
              type: "string",
              isResizable: true,
            },
            {
              fieldName: "currencyCode",
              key: "CurrencyCode",
              name: "Currency Code",
              type: "string",
              isResizable: true,
            },
            {
              fieldName: "isActive",
              key: "isActive",
              name: "Status",
              isResizable: true,
              type: "string",
              onRender: (item: any) =>
                item.isActive === "false" ? (
                  <span style={CustomSpanForListing}>Inactive</span>
                ) : (
                  <span style={CustomSpanForListing}>Active</span>
                ),
            },
          ]}
          selection={countryProfileSelection}
          selectionMode={SelectionMode.single}
          selectionPreservedOnEmptyClick
          setKey="COUNTRYPROFILE_LIST_DATA_GRID_SORTABLE_SELECTABLE"
          // onActiveItemChanged={(item) => checkedItem(item)}
        />
      </Stack>
    </Stack>
  );
};

export default CountryProfileList;
